// voiceSessionClose — abrupt-close resilience for voice_sessions (issue #378).
//
// PROBLEM: useRealtimeVoice's teardown() closes the voice_sessions row with a
// deliberately fire-and-forget supabase-js UPDATE. That works for every
// in-app exit path (hangup, 10-min timer, error, unmount) — production rows
// confirm it. It does NOT survive the page/webview being killed mid-call
// (force-quitting the app, swiping it away): the request never flushes and
// the row is stranded with ended_at = NULL, duration_seconds = NULL.
//
// WHY THAT MATTERS: voice_seconds_used_today() does
// coalesce(sum(duration_seconds), 0), so a NULL row contributes ZERO to the
// 20 min/day cap. A user who habitually force-closes is effectively uncapped
// on paid OpenAI realtime minutes.
//
// WHY keepalive fetch AND NOT navigator.sendBeacon: sendBeacon cannot set
// request headers. PostgREST needs `Authorization: Bearer <jwt>` and `apikey`,
// and voice_sessions RLS gates UPDATE on auth.uid() = user_id — a beacon
// would arrive unauthenticated and be rejected. `fetch(..., {keepalive:true})`
// carries headers and is handed to the browser's background-request queue, so
// it outlives the document. This is the same technique src/lib/analytics.ts
// (:139-161) already uses to close out user_sessions on unload.
//
// This module is a pure-ish helper on purpose: the payload math and the
// request shape are unit-testable without dragging in the (locked-zone)
// WebRTC hook.

export interface VoiceSessionClosePayload {
  ended_at: string;
  duration_seconds: number;
}

/**
 * The exact same payload teardown() writes — ended_at as an ISO instant and
 * duration_seconds anchored to the moment the server minted the row. Kept
 * identical on purpose so a snapshot and a normal close can never disagree
 * about how a duration is computed.
 */
export function buildVoiceSessionClosePayload(
  startedAtMs: number,
  nowMs: number,
): VoiceSessionClosePayload {
  return {
    ended_at: new Date(nowMs).toISOString(),
    duration_seconds: Math.max(0, Math.round((nowMs - startedAtMs) / 1000)),
  };
}

export interface VoiceSessionCloseSnapshotArgs {
  sessionId: string | null;
  /** When the server's voice_sessions row set started_at (mint success). */
  startedAtMs: number | null;
  /** Read synchronously from the already-resolved auth session — never awaited
   *  inside an unload handler, or the page dies before the request is built. */
  accessToken: string | undefined;
  nowMs: number;
}

/**
 * Fire-and-forget PATCH of ended_at/duration_seconds that survives the
 * document being torn down. Returns true if a request was actually issued
 * (used by tests and by the caller's debounce), false if it no-op'd.
 *
 * Guards, all load-bearing:
 * - no sessionId → nothing to close.
 * - no startedAtMs → teardown() already ran and nulled it. Writing here would
 *   stamp duration_seconds = 0 OVER a correct duration. Never do that.
 * - no accessToken → RLS would reject the write anyway.
 */
export function sendVoiceSessionCloseSnapshot({
  sessionId,
  startedAtMs,
  accessToken,
  nowMs,
}: VoiceSessionCloseSnapshotArgs): boolean {
  if (!sessionId || typeof startedAtMs !== "number" || !accessToken) return false;

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const apikey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  if (!supabaseUrl || !apikey || typeof fetch === "undefined") return false;

  try {
    fetch(
      `${supabaseUrl}/rest/v1/voice_sessions?id=eq.${encodeURIComponent(sessionId)}`,
      {
        method: "PATCH",
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
          apikey,
          Authorization: `Bearer ${accessToken}`,
          Prefer: "return=minimal",
        },
        body: JSON.stringify(buildVoiceSessionClosePayload(startedAtMs, nowMs)),
      },
    ).catch(() => {
      // The page is going away. There is no one left to tell.
    });
    return true;
  } catch (_) {
    // fetch threw synchronously (no keepalive support, blocked request) —
    // the server-side reaper (close_stale_voice_sessions) is the backstop.
    return false;
  }
}
